import type { IConfig } from '@cian/config/shared';

/**
 * Настройки сбора метрик карты, которые можно менять через Runtime Settings.
 *
 * Причина выносить их наружу простая: транспорт телеметрии ничего не агрегирует на клиенте,
 * и при неудачной раскатке мы можем залить приёмник. Чинить это релизом слишком долго.
 */
export const TELEMETRY_CONFIG_KEYS = {
  /** Главный рубильник: false полностью выключает отправку метрик карты */
  enabled: 'mapSearchFrontend.telemetry.enabled',
  /** Доля сессий, с которых вообще собираются метрики */
  sessionSamplingRate: 'mapSearchFrontend.telemetry.sessionSamplingRate',
  /** Доля отрисовок, попадающих в метрику. 0 выключает только высокочастотные замеры */
  renderSamplingRate: 'mapSearchFrontend.telemetry.renderSamplingRate',
  /** Окно тишины после действия с картой, по которому определяется окончание взаимодействия */
  interactionQuietWindowMs: 'mapSearchFrontend.telemetry.interactionQuietWindowMs',
} as const;

export interface IMapTelemetryConfig {
  enabled: boolean;
  sessionSamplingRate: number;
  renderSamplingRate: number;
  interactionQuietWindowMs: number;
}

/**
 * Значения по умолчанию. Действуют, пока настройки в Runtime Settings не заведены
 * или содержат мусор.
 *
 * Собираем со всех сессий: по логам nginx в загруженный час страницу карты открывают около семи
 * раз в секунду, то есть полный сбор добавляет browser-telemetry порядка 12% запросов к его
 * текущей нагрузке, а с тройным запасом на пик — около 36%. Это подъёмно.
 *
 * Доля сессий остаётся ручкой на случай, если приёмнику станет тяжело: она режет и запросы,
 * и метрики разом, в отличие от renderSamplingRate, который трогает только замеры отрисовки.
 */
export const TELEMETRY_CONFIG_DEFAULTS: IMapTelemetryConfig = Object.freeze({
  enabled: true,
  sessionSamplingRate: 1,
  renderSamplingRate: 0.01,
  interactionQuietWindowMs: 200,
});

/** Верхняя граница окна тишины: больше секунды замер перестаёт отражать взаимодействие */
const MAX_QUIET_WINDOW_MS = 1000;

function readBoolean(config: IConfig, key: string, fallback: boolean): boolean {
  const value = config.get<boolean>(key);

  return typeof value === 'boolean' ? value : fallback;
}

function readNumber(
  config: IConfig,
  key: string,
  fallback: number,
  { min, max }: { min: number; max: number },
): number {
  const value = config.get<number>(key);

  /**
   * Настройку в Runtime Settings правит человек, и опечатка не должна ломать сбор метрик
   * или, что хуже, отключать сэмплирование.
   */
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    return fallback;
  }

  return value;
}

/**
 * Конфиг в браузере — снимок, вложенный в страницу при серверном рендере, и до перезагрузки
 * он не меняется. Поэтому разбираем его один раз на экземпляр конфига, а не на каждое создание
 * фасада: фасад создаётся на каждый запрос кластеров, то есть на каждое движение карты.
 *
 * Ключ — сам объект конфига, так что в тестах каждый стаб получает свой разбор,
 * а после выгрузки страницы запись уходит вместе с ним.
 */
const resolvedConfigCache = new WeakMap<IConfig, IMapTelemetryConfig>();

export function getMapTelemetryConfig(config?: IConfig): IMapTelemetryConfig {
  if (!config) {
    return TELEMETRY_CONFIG_DEFAULTS;
  }

  const cached = resolvedConfigCache.get(config);

  if (cached) {
    return cached;
  }

  const resolved: IMapTelemetryConfig = {
    enabled: readBoolean(config, TELEMETRY_CONFIG_KEYS.enabled, TELEMETRY_CONFIG_DEFAULTS.enabled),
    sessionSamplingRate: readNumber(
      config,
      TELEMETRY_CONFIG_KEYS.sessionSamplingRate,
      TELEMETRY_CONFIG_DEFAULTS.sessionSamplingRate,
      { min: 0, max: 1 },
    ),
    renderSamplingRate: readNumber(
      config,
      TELEMETRY_CONFIG_KEYS.renderSamplingRate,
      TELEMETRY_CONFIG_DEFAULTS.renderSamplingRate,
      { min: 0, max: 1 },
    ),
    interactionQuietWindowMs: readNumber(
      config,
      TELEMETRY_CONFIG_KEYS.interactionQuietWindowMs,
      TELEMETRY_CONFIG_DEFAULTS.interactionQuietWindowMs,
      { min: 0, max: MAX_QUIET_WINDOW_MS },
    ),
  };

  resolvedConfigCache.set(config, resolved);

  return resolved;
}
